/* ------------------------------------------------------------------ */
/*  Appointment reminder body-param formatter (PRD.221)               */
/*                                                                    */
/*  APPOINTMENT_REMINDER is a 2-variable template:                    */
/*    {{1}} = appointment date → "MMM d, yyyy"  e.g. "May 14, 2026"   */
/*    {{2}} = appointment time → "h:mm a"       e.g. "3:00 PM"        */
/* ------------------------------------------------------------------ */

/**
 * Clinic/org timezone the appointment wall-clock values are assumed to be
 * expressed in. There is no org-timezone setting wired up yet, so this is
 * the documented fallback rather than an applied conversion (see below).
 */
export const DEFAULT_CLINIC_TIMEZONE = 'Asia/Manila';

/** Locale used to render the reminder body params. */
export const REMINDER_LOCALE = 'en-PH';

/**
 * Format an appointment's date + start time into the two positional body
 * params the APPOINTMENT_REMINDER template expects.
 *
 * `date` is "YYYY-MM-DD" and `startTime` is "HH:mm" — both are already
 * wall-clock values in clinic-local time, so NO timezone conversion is
 * applied (converting a wall-clock value would shift it incorrectly). The
 * Date objects are constructed from local parts purely so `Intl` can render
 * the locale-specific format.
 *
 * Returns `null` if either input is missing/malformed so callers can disable
 * the action rather than send garbage.
 */
export function formatAppointmentReminderParams(
  date: string,
  startTime: string,
): [string, string] | null {
  if (!date || !startTime) return null;

  const dateParts = date.split('-').map(Number);
  const timeParts = startTime.split(':').map(Number);
  if (dateParts.length < 3 || timeParts.length < 2) return null;

  const [y, m, d] = dateParts;
  const [hh, mm] = timeParts;
  if ([y, m, d, hh, mm].some((n) => Number.isNaN(n))) return null;

  const dateObj = new Date(y, m - 1, d);
  const timeObj = new Date(y, m - 1, d, hh, mm);

  const datePart = new Intl.DateTimeFormat(REMINDER_LOCALE, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(dateObj);

  const timePart = new Intl.DateTimeFormat(REMINDER_LOCALE, {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  }).format(timeObj);

  return [datePart, timePart];
}

/** Stable client-reference for an appointment's WhatsApp reminder. */
export function appointmentReminderReference(appointmentId: string): string {
  return `appointment:${appointmentId}:whatsapp-reminder`;
}
